import { useCallback, useEffect, useRef, useState } from "react";
import { Visibility } from "../types/subscription";
import { ProductMetadata } from "../types/productMetadata";
import {
  ProductSearchItem,
  ActiveContractCompositionEntry,
  ActiveBasketCompositionEntry,
  PortfolioSearchSnapshot,
  BasketSearchSnapshot,
  PortfolioActiveComposition,
  BasketActiveComposition,
  ProductExposure,
  ProductPerformance,
  ChangeSummary,
  ProductRestrictionFlag,
  isNewSnapshotShape,
} from "../types/productV3";
import { ProductRiskMetrics } from "../types/productRiskMetrics";
import { Contract } from "../types/contract";
import { productSearchService } from "../services/productSearchService";
import { contractsV2Service } from "../services/contractsV2Service";
import {
  optionChainsV2Service,
  OptionChainContract,
} from "../services/optionChainsV2Service";
import {
  discoverFactorsService,
  DiscoverFactorEntry,
  DiscoverFactorProductType,
} from "../services/discoverFactorsService";

export interface DiscoverV4DetailProduct {
  productType: "BASKET" | "PORTFOLIO";
  productId: number;
  shareToken?: string | null;
  providerId: number;
  title: string;
  description: string | null;
  providerName: string;
  visibility: Visibility;
  performance?: ProductPerformance | null;
  riskMetrics?: ProductRiskMetrics | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  numOfStocks: number;
  numOfOptions: number;
  numOfBaskets: number;
  // PWA-2242 change-history summary fields (carried from the search item).
  changeSummary?: ChangeSummary | null;
  hasPendingChanges?: boolean;
  // Contract remarks aggregated across holdings; drives per-customer trading
  // eligibility (see utils/productTradability). Carried from the search item.
  contractRemarks?: string[] | null;
  // Viewer-specific restriction annotation from investor product search.
  restrictionFlags?: ProductRestrictionFlag[] | null;
}

export interface DiscoverV4Stock {
  conId: number;
  ticker: string;
  name: string;
  exchange: string;
  weight: number;
  position: "LONG" | "SHORT";
}

export interface DiscoverV4BasketRef {
  basketId: number;
  basketName: string;
  weight: number;
  position: "LONG" | "SHORT";
  stocks: DiscoverV4Stock[];
}

export interface DiscoverV4Option {
  conId: number;
  symbol: string;
  name: string;
  exchange: string;
  expiration: string;
  strike: number;
  right: string;
  weight: number;
  position: "LONG" | "SHORT";
}

export interface DiscoverV4Composition {
  stocks: DiscoverV4Stock[];
  baskets: DiscoverV4BasketRef[];
  options: DiscoverV4Option[];
}

export interface UseDiscoverV4DetailParams {
  productType: "basket" | "portfolio";
  providerId: number;
  productId: number;
}

export interface UseDiscoverV4DetailResult {
  product: DiscoverV4DetailProduct | null;
  isLoading: boolean;
  error: string | null;
  factors: DiscoverFactorEntry[];
  isLoadingFactors: boolean;
  tags: ProductMetadata | null;
  exposure: ProductExposure | null;
  composition: DiscoverV4Composition | null;
  isLoadingComposition: boolean;
  isLocked: boolean;
  isPartial: boolean;
  retry: () => void;
}

function positionFromWeight(weight: number): "LONG" | "SHORT" {
  return weight >= 0 ? "LONG" : "SHORT";
}

function enrichStocks(
  entries: ActiveContractCompositionEntry[],
  contractMap: Map<number, Contract>
): DiscoverV4Stock[] {
  return entries.map((e) => {
    const c = contractMap.get(e.conId);
    return {
      conId: e.conId,
      ticker: c?.symbol ?? "",
      name: c?.longName ?? "",
      exchange: c?.primaryExchange ?? c?.exchange ?? "",
      weight: e.weight,
      position: positionFromWeight(e.weight),
    };
  });
}

function enrichOptions(
  entries: ActiveContractCompositionEntry[],
  contractMap: Map<number, Contract>,
  optionChainMap: Map<number, OptionChainContract>
): DiscoverV4Option[] {
  return entries.map((e) => {
    const c = contractMap.get(e.conId);
    const oc = optionChainMap.get(e.conId);
    return {
      conId: e.conId,
      symbol: c?.symbol ?? oc?.symbol ?? "",
      name: c?.longName ?? c?.symbol ?? oc?.symbol ?? "",
      exchange: c?.primaryExchange ?? c?.exchange ?? oc?.exchange ?? "",
      expiration: oc?.expiryDateLocal ?? "",
      strike: oc?.strikePrice ?? 0,
      right: oc?.putCall ?? "",
      weight: e.weight,
      position: positionFromWeight(e.weight),
    };
  });
}

function mapBaskets(
  entries: ActiveBasketCompositionEntry[],
  enrichedMap?: Map<number, { name: string; stocks: DiscoverV4Stock[] }>
): DiscoverV4BasketRef[] {
  return entries.map((e) => {
    const enriched = enrichedMap?.get(e.basketId);
    return {
      basketId: e.basketId,
      basketName: enriched?.name ?? `Basket ${e.basketId}`,
      weight: e.weight,
      position: positionFromWeight(e.weight),
      stocks: enriched?.stocks ?? [],
    };
  });
}

async function enrichBasketRefs(
  entries: ActiveBasketCompositionEntry[],
  providerId?: number
): Promise<Map<number, { name: string; stocks: DiscoverV4Stock[] }>> {
  const result = new Map<number, { name: string; stocks: DiscoverV4Stock[] }>();
  const searchPromises = entries.map((e) =>
    productSearchService
      .searchProducts({ productId: e.basketId, productType: "BASKET", providerId, pageSize: 1 })
      .then((res) => ({ basketId: e.basketId, item: res.items?.[0] ?? null }))
      .catch(() => ({ basketId: e.basketId, item: null }))
  );

  const results = await Promise.all(searchPromises);

  const allConIds: number[] = [];
  const basketConIdMap = new Map<number, ActiveContractCompositionEntry[]>();

  for (const { basketId, item } of results) {
    if (!item) continue;
    const snap = getSnapshotComposition(item);
    basketConIdMap.set(basketId, snap.stocks);
    for (const s of snap.stocks) {
      allConIds.push(s.conId);
    }
    result.set(basketId, { name: item.title, stocks: [] });
  }

  if (allConIds.length > 0) {
    const uniqueConIds = [...new Set(allConIds)];
    const contracts = await contractsV2Service
      .findContractsByConIds(uniqueConIds)
      .catch(() => [] as Contract[]);
    const contractMap = new Map<number, Contract>();
    for (const c of contracts) {
      contractMap.set(c.conId, c);
    }

    for (const [basketId, stockEntries] of basketConIdMap) {
      const enriched = result.get(basketId);
      if (enriched) {
        enriched.stocks = enrichStocks(stockEntries, contractMap);
      }
    }
  }

  return result;
}

function extractProduct(item: ProductSearchItem): DiscoverV4DetailProduct {
  return {
    productType: item.productType,
    productId: item.productId,
    shareToken: item.shareToken ?? null,
    providerId: item.providerId,
    title: item.title,
    description: item.description,
    providerName: item.providerName,
    visibility: item.visibility,
    performance: item.performance,
    riskMetrics: item.riskMetrics ?? null,
    createdAtUtc: item.createdAtUtc,
    updatedAtUtc: item.updatedAtUtc,
    numOfStocks: item.numOfStocks,
    numOfOptions: item.numOfOptions,
    numOfBaskets: item.numOfBaskets,
    changeSummary: item.changeSummary ?? null,
    hasPendingChanges: item.data?.hasPendingChanges ?? false,
    contractRemarks: item.contractRemarks ?? [],
    restrictionFlags: item.restrictionFlags ?? [],
  };
}

function extractTags(item: ProductSearchItem): ProductMetadata | null {
  if (item.settings && typeof item.settings === "object") {
    const s = item.settings as ProductMetadata;
    if (s.risk_tag !== undefined || s.investment_strategy_tag !== undefined || s.min_nlv_tag !== undefined || s.investible_tag !== undefined) {
      return s;
    }
  }
  return null;
}

// `performanceDrifts` is structurally `{contracts:{stocks:[],options:[]},baskets:[]}`
// when the BE returns a partial (subscriber-gated) snapshot. The original `??`
// fallback was load-bearing only for null/undefined, so the empty object would
// win and the populated `activated` was discarded.
function hasComposition(
  c: PortfolioActiveComposition | BasketActiveComposition | undefined
): boolean {
  if (!c) return false;
  let total = c.contracts.stocks.length;
  if ("options" in c.contracts) total += c.contracts.options.length;
  if ("baskets" in c) total += c.baskets.length;
  return total > 0;
}

export function getSnapshotComposition(item: ProductSearchItem): {
  stocks: ActiveContractCompositionEntry[];
  options: ActiveContractCompositionEntry[];
  baskets: ActiveBasketCompositionEntry[];
} {
  const empty = { stocks: [], options: [], baskets: [] };
  if (!isNewSnapshotShape(item.snapshot)) return empty;

  if (item.productType === "BASKET") {
    const snap = item.snapshot as BasketSearchSnapshot;
    const source = hasComposition(snap.performanceDrifts) ? snap.performanceDrifts : snap.activated;
    return {
      stocks: source?.contracts?.stocks ?? [],
      options: [],
      baskets: [],
    };
  }

  const snap = item.snapshot as PortfolioSearchSnapshot;
  const source = hasComposition(snap.performanceDrifts) ? snap.performanceDrifts : snap.activated;
  return {
    stocks: source?.contracts?.stocks ?? [],
    options: source?.contracts?.options ?? [],
    baskets: source?.baskets ?? [],
  };
}

export function getSnapshotPartial(item: ProductSearchItem): boolean {
  if (!isNewSnapshotShape(item.snapshot)) return false;
  const snap = item.snapshot as PortfolioSearchSnapshot | BasketSearchSnapshot;
  return snap.partial === true;
}

export function useDiscoverV4Detail(
  params: UseDiscoverV4DetailParams
): UseDiscoverV4DetailResult {
  const { productType, providerId, productId } = params;

  const [product, setProduct] = useState<DiscoverV4DetailProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [factors, setFactors] = useState<DiscoverFactorEntry[]>([]);
  const [isLoadingFactors, setIsLoadingFactors] = useState(true);

  const [tags, setTags] = useState<ProductMetadata | null>(null);
  const [exposure, setExposure] = useState<ProductExposure | null>(null);

  const [composition, setComposition] = useState<DiscoverV4Composition | null>(null);
  const [isLoadingComposition, setIsLoadingComposition] = useState(true);
  const [isPartial, setIsPartial] = useState(false);

  const isLocked = false; // Admin-only page; no subscription gating

  const fetchIdRef = useRef(0);
  const [retryTrigger, setRetryTrigger] = useState(0);

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setIsLoadingFactors(true);
    setIsLoadingComposition(true);
    setError(null);
    setProduct(null);
    setComposition(null);
    setTags(null);
    setExposure(null);
    setFactors([]);
    setIsPartial(false);

    const upperType = productType.toUpperCase() as "BASKET" | "PORTFOLIO";
    const factorType: DiscoverFactorProductType = productType;

    const searchPromise = productSearchService.searchProducts({
      providerId,
      productId,
      productType: upperType,
      pageSize: 1,
    });

    const factorsPromise = discoverFactorsService
      .getFactors(providerId, factorType, productId)
      .catch(() => [] as DiscoverFactorEntry[]);

    try {
      const [searchResult, rawFactors] = await Promise.all([
        searchPromise,
        factorsPromise,
      ]);

      if (fetchId !== fetchIdRef.current) return;

      if (!searchResult.items || searchResult.items.length === 0) {
        setError("Product not found.");
        setIsLoading(false);
        setIsLoadingFactors(false);
        setIsLoadingComposition(false);
        return;
      }

      const item = searchResult.items[0];
      setProduct(extractProduct(item));
      setTags(extractTags(item));
      setExposure(item.exposure ?? null);
      setIsPartial(getSnapshotPartial(item));
      setIsLoading(false);

      setFactors(rawFactors);
      setIsLoadingFactors(false);

      // Enrich composition with contract metadata + basket details
      const snapshot = getSnapshotComposition(item);
      const allConIds = [
        ...snapshot.stocks.map((s) => s.conId),
        ...snapshot.options.map((o) => o.conId),
      ];

      // Fetch contract details, option chain details, and basket details in parallel
      const contractsPromise =
        allConIds.length > 0
          ? contractsV2Service.findContractsByConIds(allConIds).catch(() => [] as Contract[])
          : Promise.resolve([] as Contract[]);

      const optionConIds = snapshot.options.map((o) => o.conId);
      const optionChainsPromise =
        optionConIds.length > 0
          ? optionChainsV2Service
              .findOptionContractsByConIds(optionConIds)
              .then((res) => res.optionContracts)
              .catch(() => [] as OptionChainContract[])
          : Promise.resolve([] as OptionChainContract[]);

      const basketEnrichPromise =
        snapshot.baskets.length > 0
          ? enrichBasketRefs(snapshot.baskets, providerId).catch(() => new Map<number, { name: string; stocks: DiscoverV4Stock[] }>())
          : Promise.resolve(new Map<number, { name: string; stocks: DiscoverV4Stock[] }>());

      try {
        const [contracts, optionChainContracts, basketEnrichedMap] = await Promise.all([
          contractsPromise,
          optionChainsPromise,
          basketEnrichPromise,
        ]);
        if (fetchId !== fetchIdRef.current) return;

        const contractMap = new Map<number, Contract>();
        for (const c of contracts) {
          contractMap.set(c.conId, c);
        }

        const optionChainMap = new Map<number, OptionChainContract>();
        for (const oc of optionChainContracts) {
          optionChainMap.set(oc.conId, oc);
        }

        setComposition({
          stocks: enrichStocks(snapshot.stocks, contractMap),
          options: enrichOptions(snapshot.options, contractMap, optionChainMap),
          baskets: mapBaskets(snapshot.baskets, basketEnrichedMap),
        });
      } catch {
        if (fetchId !== fetchIdRef.current) return;
        setComposition({
          stocks: snapshot.stocks.map((s) => ({
            conId: s.conId,
            ticker: "",
            name: "",
            exchange: "",
            weight: s.weight,
            position: positionFromWeight(s.weight),
          })),
          options: snapshot.options.map((o) => ({
            conId: o.conId,
            symbol: "",
            name: "",
            exchange: "",
            expiration: "",
            strike: 0,
            right: "",
            weight: o.weight,
            position: positionFromWeight(o.weight),
          })),
          baskets: mapBaskets(snapshot.baskets),
        });
      }
      setIsLoadingComposition(false);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;

      const isAbort =
        err instanceof Error && err.name === "AbortError";
      if (isAbort) return;

      console.error("[DiscoverV4Detail] fetch failed:", err);
      setError("Failed to load product details. Please try again.");
      setIsLoading(false);
      setIsLoadingFactors(false);
      setIsLoadingComposition(false);
    }
  }, [productType, providerId, productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, retryTrigger]);

  const retry = useCallback(() => {
    setRetryTrigger((prev) => prev + 1);
  }, []);

  return {
    product,
    isLoading,
    error,
    factors,
    isLoadingFactors,
    tags,
    exposure,
    composition,
    isLoadingComposition,
    isLocked,
    isPartial,
    retry,
  };
}
